import { useState, useEffect, useRef } from "react";
import { MapPin, Plus, Pencil, X, AlertCircle } from "lucide-react";
import { Address } from "./types";
import InputField from "@/components/form/InputField";
import { CustomButton } from "@/components/ui/Buttons";
import Modal from "@/components/ui/Modal";
import { cn } from "@/lib/utils";
import { handleApiRequest } from "@/utils/handleApiRequest";
import toast from "react-hot-toast";
import {
  useDeleteShippingAddressesMutation,
  useEditShippingAddressesMutation,
  useGetShippingAddressesQuery,
  useUploadShippingAddressesMutation,
} from "@/redux/feature/shippingAddress/shippingAddressApi";
import { useGlobalContext } from "@/context/GlobalContext";
import {
  AddNewLocation,
  TCreatorShippingData,
} from "@/pages/orderMananagement/stages/PlanningStage/ProductShipping/ProductShipping";

interface AddressSectionProps {
  // addresses: Address[];
  selectedAddressId: string | null;
  onSelectAddress: (id: string | null) => void;
  onAddAddress: (address: Omit<Address, "id">) => void;
}

const AddressSection = ({
  selectedAddressId,
  onSelectAddress,
  onAddAddress,
}: AddressSectionProps) => {
  const { userData } = useGlobalContext();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showSingleAddressConfirm, setShowSingleAddressConfirm] = useState(false);
  const hasAutoSelectedRef = useRef(false);

  // Fetch existing addresses via API
  const { data } = useGetShippingAddressesQuery(
    encodeURIComponent(userData?.email),
  );
  const locations: TCreatorShippingData[] = data?.data || [];

  // Hooks for Edit and Delete operations
  const [editShippingAddress, { isLoading: isEditing }] =
    useEditShippingAddressesMutation();
  const [deleteShippingAddress, { isLoading: isDeleting }] =
    useDeleteShippingAddressesMutation();

  const handleEditAddress = (addressId: string) => {
    // Here you can open the modal to edit the address, for example:
    const addressToEdit = locations.find((loc) => loc._id === addressId);
    if (addressToEdit) {
      // open modal and pre-populate with addressToEdit
      console.log("Edit this address", addressToEdit);
      // You can use a modal to handle address editing here
    }
  };

  const handleDeleteAddress = (addressId: string) => {
    if (confirm("Are you sure you want to delete this address?")) {
      deleteShippingAddress(addressId)
        .unwrap()
        .then(() => {
          toast.success("Address deleted successfully!");
          // If deleted address was selected, clear selection
          if (selectedAddressId === addressId) {
            onSelectAddress(null);
          }
        })
        .catch(() => {
          toast.error("Failed to delete the address.");
        });
    }
  };

  // Handle single address confirmation
  useEffect(() => {
    // Only run when addresses are loaded and we haven't auto-selected yet
    if (locations.length === 1 && !hasAutoSelectedRef.current && !selectedAddressId) {
      setShowSingleAddressConfirm(true);
      hasAutoSelectedRef.current = true;
    } else if (locations.length !== 1) {
      // Reset if address count changes
      hasAutoSelectedRef.current = false;
      setShowSingleAddressConfirm(false);
    }
  }, [locations.length, selectedAddressId]);

  const handleConfirmSingleAddress = () => {
    if (locations.length === 1) {
      onSelectAddress(locations[0]._id);
      setShowSingleAddressConfirm(false);
      toast.success("Address selected successfully");
    }
  };

  const handleRejectSingleAddress = () => {
    setShowSingleAddressConfirm(false);
    hasAutoSelectedRef.current = false;
  };

  const handleAddressAdded = () => {
    setIsAddModalOpen(false);
    // The query will refetch automatically, triggering the single address logic if needed
  };

  const hasMultipleAddresses = locations.length > 1;
  const hasNoSelection = !selectedAddressId && locations.length > 0;
  const hasNoAddresses = locations.length === 0;

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2.5">
        <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <span className="block text-sm font-medium text-foreground">
            Home address
          </span>
          <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
            Required for shipping in videos involving real products
          </span>
        </div>
      </div>

      {/* No Addresses Warning */}
      {hasNoAddresses && (
        <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-800 dark:text-amber-200">
              Please add at least one address to continue. Address is required for same-day delivery.
            </p>
          </div>
        </div>
      )}

      {/* Single Address Confirmation Prompt */}
      {showSingleAddressConfirm && locations.length === 1 && (
        <div className="p-4 rounded-lg border border-accent/30 bg-accent/5">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground mb-2">
                Confirm Address Selection
              </p>
              <p className="text-xs text-muted-foreground mb-3">
                You have one saved address. Would you like to use this address for same-day delivery?
              </p>
              <div className="p-3 rounded-md bg-background border border-border mb-3">
                <p className="text-xs font-medium text-foreground mb-1">
                  {locations[0].location_name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {locations[0].address}, {locations[0].city}, {locations[0].state} {locations[0].pin_code}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <CustomButton
                  className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-white text-sm px-3 py-1.5 whitespace-normal"
                  onClick={handleConfirmSingleAddress}
                >
                  Yes, Use This Address
                </CustomButton>
                <CustomButton
                  variant="outline"
                  className="w-full sm:w-auto text-sm px-3 py-1.5 whitespace-normal"
                  onClick={handleRejectSingleAddress}
                >
                  No, I'll Select Later
                </CustomButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Multiple Addresses Warning */}
      {hasMultipleAddresses && hasNoSelection && !showSingleAddressConfirm && (
        <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-800 dark:text-amber-200">
              Please select an address from the options below to continue.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {locations.map((address) => (
          <div
            key={address._id}
            onClick={() =>
              onSelectAddress(
                selectedAddressId === address._id ? null : address._id,
              )
            }
            className={cn(
              "relative select-none cursor-pointer w-full sm:flex-1 sm:min-w-[200px] sm:max-w-[280px] p-4 rounded-lg border text-left transition-all duration-200",
              selectedAddressId === address._id
                ? "border-accent bg-accent/5"
                : "border-border hover:border-primary/30",
            )}
          >
            <div className="flex items-start justify-between mb-2">
              <span
                className={cn(
                  "text-sm font-semibold uppercase",
                  selectedAddressId === address._id
                    ? "text-accent"
                    : "text-primary",
                )}
              >
                {address.location_name}
              </span>
              <div className="flex z-30 relative gap-2">
                {/* <button
                  onClick={() => handleEditAddress(address._id)}
                  disabled={isEditing}
                >
                  <Pencil className="w-4 h-4 text-accent" />
                </button> */}
                <button
                  onClick={() => handleDeleteAddress(address._id)}
                  disabled={isDeleting}
                >
                  <X className="w-4 h-4 text-red-600" />
                </button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {address.address}, {address.city}, {address.state}{" "}
              {address.pin_code}
            </p>
          </div>
        ))}

        {/* Add Address Button */}
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="w-full sm:flex-1 sm:min-w-[140px] sm:max-w-[160px] p-4 rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-all duration-200 flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <Plus className="w-5 h-5" />
          <span className="text-sm font-medium">Add Address</span>
        </button>
      </div>

      {/* Add Address Modal */}
      <Modal
        width="571px"
        padding="0px"
        setOpen={setIsAddModalOpen}
        isOpen={isAddModalOpen}
      >
        <AddNewLocation onClose={handleAddressAdded} />
      </Modal>
    </div>
  );
};

export default AddressSection;
